import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, File, X, AlertCircle } from 'lucide-react';

const FileUpload = ({ 
  onFileSelect, 
  acceptedTypes = '.pdf,.jpg,.jpeg,.png,.doc,.docx',
  maxSizeMB = 10,
  placeholder,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // 'uploading', 'success', 'error'
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(t('upload.fileSize', { size: maxSizeMB }));
      return false;
    }

    // Check file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const acceptedExtensions = acceptedTypes.split(',').map(type => type.trim().toLowerCase());
    
    if (!acceptedExtensions.includes(fileExtension)) {
      setError(t('upload.fileType', { types: acceptedTypes }));
      return false;
    }

    return true;
  };

  const handleFileSelect = (file) => {
    setError('');
    
    if (!validateFile(file)) {
      return;
    }

    setSelectedFile(file);
    
    // Create a mock upload process (replace with actual upload logic)
    setUploadStatus('uploading');
    
    // Simulate upload process - replace with actual API call
    setTimeout(() => {
      const mockFileUrl = `https://uploads.successclub.com/${Date.now()}-${file.name}`;
      setUploadStatus('success');
      onFileSelect && onFileSelect(file, mockFileUrl);
    }, 2000);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setUploadStatus(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`file-upload ${className}`}>
      {!selectedFile ? (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragging 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-8 h-8 mx-auto mb-3 text-gray-400" />
          <p className="text-sm text-gray-600 mb-2">
            {placeholder || t('upload.dragDrop')}
          </p>
          <p className="text-xs text-gray-500">
            {t('upload.fileType', { types: acceptedTypes.replace(/\./g, '').toUpperCase() })}
          </p>
          <p className="text-xs text-gray-500">
            {t('upload.fileSize', { size: maxSizeMB })}
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes}
            onChange={handleInputChange}
            className="hidden"
          />
        </div>
      ) : (
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <File className="w-5 h-5 text-blue-600" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {uploadStatus === 'uploading' && (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-xs">{t('upload.uploading')}</span>
                </div>
              )}
              
              {uploadStatus === 'success' && (
                <div className="flex items-center gap-1 text-green-600">
                  <div className="w-4 h-4 rounded-full bg-green-600 flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-xs">{t('upload.uploadSuccess')}</span>
                </div>
              )}
              
              <button
                onClick={clearFile}
                className="text-gray-400 hover:text-gray-600 p-1"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="mt-2 flex items-center gap-2 text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;